import { buildErrorEventProps } from '~/analytics/errorEventProps';
import { captureError, captureException } from '~/analytics/posthog';

interface ErrorExtraData {
  status?: number;
  data?: any;
  user?: { id?: string; email?: string };
  [key: string]: any;
}

function sanitizeCardData(data: any): any {
  if (!data) return data;

  const isCardError =
    data?.axiosRaw?.config?.data &&
    (data.erros?.['request.card'] ||
      (typeof data.axiosRaw.config.data === 'string' && data.axiosRaw.config.data.includes('"type":"card"')));

  if (!isCardError) return data;

  const sanitizedData: any = {
    message: data.message || undefined,
    erros: {}
  };

  if (data.erros?.['request.card']?.[0]) {
    sanitizedData.erros['request.card'] = [data.erros['request.card'][0]];
  }

  if (data.axiosRaw) {
    sanitizedData.axiosRaw = {
      message: data.axiosRaw.message || undefined,
      status: data.axiosRaw.status || undefined,
      config: {
        url: data.axiosRaw.config?.url || undefined
      }
    };

    if (data.axiosRaw.config?.data) {
      try {
        const requestData =
          typeof data.axiosRaw.config.data === 'string'
            ? JSON.parse(data.axiosRaw.config.data)
            : data.axiosRaw.config.data;

        if (requestData) {
          const safeRequestData: any = {
            type: requestData.type || undefined
          };

          if (requestData.card) {
            safeRequestData.card = {
              holder_name: requestData.card.holder_name || undefined,
              last_four_digits: requestData.card.last_four_digits || undefined
            };
          }

          sanitizedData.axiosRaw.config.data = JSON.stringify(safeRequestData);
        }
      } catch (e) {
        console.warn('Erro ao processar dados do cartão para o PostHog:', e);
      }
    }
  }

  return sanitizedData;
}

function sanitizeError(error: any): any {
  if (!error) return error;

  // O spread não copia name/message/stack do Error (não enumeráveis).
  const sanitizedError = { ...error, name: error?.name, message: error?.message, stack: error?.stack };

  if (error?.isAxiosError) {
    if (error?.response?.data?.request?.card) {
      sanitizedError.response = {
        ...error.response,
        data: {
          ...error.response.data,
          request: {
            ...error.response.data.request,
            card: {
              holder_name: error.response.data.request.card.holder_name || undefined,
              last_four_digits: error.response.data.request.card.last_four_digits || undefined
            }
          }
        }
      };
    }

    if (error?.config?.data) {
      try {
        const configData = typeof error.config.data === 'string' ? JSON.parse(error.config.data) : error.config.data;

        if (configData?.type === 'card' && configData?.card) {
          const safeCard = {
            holder_name: configData.card.holder_name || undefined
          };

          const safeConfigData = {
            type: configData.type,
            card: safeCard
          };

          sanitizedError.config = {
            ...error.config,
            data: JSON.stringify(safeConfigData)
          };
        }
      } catch (e) {
        console.warn('Erro ao sanitizar config.data:', e);
      }
    }
  }

  return sanitizedError;
}

/** Error só com nome, mensagem e stack: nada de `config`/`response` (corpo do request, cartão) no Error Tracking. */
function toSafeException(message: string, error: any): Error {
  const exception = new Error(typeof error?.message === 'string' && error.message ? error.message : message);
  if (typeof error?.name === 'string' && error.name) exception.name = error.name;
  if (typeof error?.stack === 'string') exception.stack = error.stack;
  return exception;
}

/**
 * Reporta um erro ao PostHog. O nome do evento é a própria `message` (a mesma string que ia para o
 * Rollbar), e a exceção vai para o Error Tracking agrupada por ela (`$exception_fingerprint`).
 * Só props enxutas e sem PII (`buildErrorEventProps`), depois da sanitização de cartão.
 */
export function reportError(message: string, error: any, extraData: ErrorExtraData = {}) {
  console.error(message, {
    error,
    extraData,
    errorDetails: {
      message: error?.message,
      stack: error?.stack,
      name: error?.name,
      url: window.location.href,
      timestamp: new Date().toISOString()
    }
  });

  const sanitizedError = sanitizeError(error);
  const sanitizedExtraData = extraData.data ? { ...extraData, data: sanitizeCardData(extraData.data) } : extraData;
  const props = buildErrorEventProps(sanitizedExtraData, sanitizedError);

  captureError(message, props);
  captureException(toSafeException(message, sanitizedError), {
    ...props,
    error_event: message,
    $exception_fingerprint: message
  });
}
